import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { FileText, Scale, AlertTriangle, Users, CreditCard, Shield } from 'lucide-react';
import SEOHead from './SEOHead';
import { getBreadcrumbStructuredData } from '../utils/structuredData';
import Header from './Header';
import Footer from './Footer';

const TermsOfService = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const breadcrumbData = getBreadcrumbStructuredData([
    { name: 'Home', url: 'https://laimu.mx/' },
    { name: 'Terms of Service', url: 'https://laimu.mx/terms' }
  ]);

  const seoTitle = language === 'en' 
    ? 'Terms of Service - LAIMU Trantor | Legal Terms & Conditions'
    : 'Términos de Servicio - LAIMU Trantor | Términos y Condiciones Legales';
    
  const seoDescription = language === 'en'
    ? 'Read LAIMU Trantor\'s terms of service and legal conditions. Understand your rights and responsibilities when using our ERP, CRM, TMS, and business management software solutions.'
    : 'Lee los términos de servicio y condiciones legales de LAIMU Trantor. Comprende tus derechos y responsabilidades al usar nuestras soluciones de software ERP, CRM, TMS y gestión empresarial.';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        url="/terms"
        structuredData={breadcrumbData}
        keywords={language === 'en' 
          ? 'terms of service, legal terms, software license, user agreement, LAIMU terms, service conditions, software usage rights, legal compliance'
          : 'términos servicio, términos legales, licencia software, acuerdo usuario, términos LAIMU, condiciones servicio, derechos uso software, cumplimiento legal'
        }
      />
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#e0f2fe' }}>
                  <FileText className="w-6 h-6" style={{ color: '#115d7b' }} />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{t.legal.terms.title}</h1>
                  <p className="text-gray-600">{t.legal.terms.lastUpdated}: {t.legal.terms.date}</p>
                </div>
              </div>

              <div className="prose max-w-none">
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Scale className="w-6 h-6 mr-2" style={{ color: '#115d7b' }} />
                    {t.legal.terms.acceptance.title}
                  </h2>
                  <p className="text-gray-700 mb-4">{t.legal.terms.acceptance.content}</p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Users className="w-6 h-6 mr-2" style={{ color: '#115d7b' }} />
                    {t.legal.terms.services.title}
                  </h2>
                  <p className="text-gray-700 mb-4">{t.legal.terms.services.intro}</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                    {t.legal.terms.services.list.map((service, index) => (
                      <li key={index}>{service}</li>
                    ))}
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                    <AlertTriangle className="w-6 h-6 mr-2" style={{ color: '#115d7b' }} />
                    {t.legal.terms.userResponsibilities.title}
                  </h2>
                  <p className="text-gray-700 mb-4">{t.legal.terms.userResponsibilities.intro}</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                    {t.legal.terms.userResponsibilities.list.map((responsibility, index) => (
                      <li key={index}>{responsibility}</li>
                    ))}
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                    <CreditCard className="w-6 h-6 mr-2" style={{ color: '#115d7b' }} />
                    {t.legal.terms.payment.title}
                  </h2>
                  <p className="text-gray-700 mb-4">{t.legal.terms.payment.content}</p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Shield className="w-6 h-6 mr-2" style={{ color: '#115d7b' }} />
                    {t.legal.terms.liability.title}
                  </h2>
                  <p className="text-gray-700 mb-4">{t.legal.terms.liability.content}</p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t.legal.terms.termination.title}</h2>
                  <p className="text-gray-700 mb-4">{t.legal.terms.termination.content}</p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t.legal.terms.changes.title}</h2>
                  <p className="text-gray-700 mb-4">{t.legal.terms.changes.content}</p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t.legal.terms.contact.title}</h2>
                  <p className="text-gray-700 mb-4">{t.legal.terms.contact.content}</p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700">
                      <strong>Email:</strong> legal@laimu.mx<br />
                      <strong>{t.contact.phone}:</strong> +52 81 1107 1450<br />
                      <strong>{t.contact.address.line1}</strong><br />
                      {t.contact.address.line2}<br />
                      {t.contact.address.city}, {t.contact.address.state} {t.contact.address.zip}
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsOfService;